import { useMemo } from 'react';

import { IFilterDefinition, OptionType } from '@evinced-private/ui-common';

import { createAsyncLoadOptions, getInitialOptions } from 'src/common/helpers/AsyncFilterHelpers';

const INITIAL_OPTIONS_COUNT = 100;

interface UseMobileScanFiltersParams<T> {
	allScans: OptionType[];
	selectedScans: OptionType[];
	filterOptions: T;
	getFilterDefinitions: (
		scansLoadOptions: (searchText: string, callback: (options: OptionType[]) => void) => void,
		scansOptions: OptionType[]
	) => IFilterDefinition[];
}

interface UseMobileScanFiltersReturn<T> {
	filterDefinitions: IFilterDefinition[];
	mergedFilterOptions: T;
}

export const useMobileScanFilters = <T extends Record<string, unknown>>({
	allScans,
	selectedScans,
	filterOptions,
	getFilterDefinitions
}: UseMobileScanFiltersParams<T>): UseMobileScanFiltersReturn<T> => {
	// Get initial options: selected + first 100 non-selected
	const initialScanOptions = useMemo(
		() => getInitialOptions(allScans, selectedScans, INITIAL_OPTIONS_COUNT),
		[allScans, selectedScans]
	);

	// Create loadOptions function that uses initialOptions when search is empty
	const scansLoadOptions = useMemo(
		() => createAsyncLoadOptions(allScans, initialScanOptions),
		[allScans, initialScanOptions]
	);

	// Get filter definitions with async loading built in
	const filterDefinitions = useMemo(
		() => getFilterDefinitions(scansLoadOptions, initialScanOptions),
		[getFilterDefinitions, scansLoadOptions, initialScanOptions]
	);

	const mergedFilterOptions = useMemo(
		() => ({
			...filterOptions,
			scans: initialScanOptions
		}),
		[filterOptions, initialScanOptions]
	) as T;

	return {
		filterDefinitions,
		mergedFilterOptions
	};
};
